'use client';

import './payment-settings.scss';

import { useTranslation } from 'react-i18next';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks.js';
import {
  commitmentsQuery,
  paymentMethodsQuery,
} from '@sola/supporter-sdk/queries.js';

import { PaymentMethodCard } from '../../molecules/payment-method-card/payment-method-card.js';
import { AddMethodSheet } from '../add-method-sheet/add-method-sheet.js';
import { SectionCardList } from '../section-card-list/section-card-list.js';
import {
  AddPaymentMethodButton,
  AddPaymentMethodButtonDescription,
  AddPaymentMethodButtonLabel,
} from './payment-settings-add.js';

/**
 * Get component data
 */
export const useComponentData = () => {
  const { data, isLoading, isFetching, isPending } = useSupporterQuery([
    paymentMethodsQuery,
    commitmentsQuery,
  ]);

  let paymentMethods: GraphQLSupporterPaymentMethod[] = [];
  let primaryMethod: GraphQLSupporterPaymentMethod | undefined;
  let otherMethods: GraphQLSupporterPaymentMethod[] = [];

  // @ts-expect-error //TODO This should be fixed once we tighten up useSupporterQuery types.
  const [methodQueryData, commitmentsQueryData] = data;
  const commitments: GraphQLCommitment[] = commitmentsQueryData?.commitments;

  if (
    Array.isArray(methodQueryData?.paymentMethods) &&
    methodQueryData.paymentMethods.length &&
    commitmentsQueryData
  ) {
    paymentMethods = methodQueryData.paymentMethods;

    const reducedMethods = paymentMethods.reduce(
      (
        acc: {
          primaryMethod: GraphQLSupporterPaymentMethod | undefined;
          otherMethods: GraphQLSupporterPaymentMethod[];
        },
        method: GraphQLSupporterPaymentMethod,
      ) => {
        if (method.isPrimary) {
          acc.primaryMethod = method;
        } else {
          acc.otherMethods.push(method);
        }
        return acc;
      },
      { primaryMethod: undefined, otherMethods: [] },
    );

    primaryMethod = reducedMethods.primaryMethod;
    otherMethods = reducedMethods.otherMethods;
  }

  return {
    isShowingSkeletons: isLoading || isFetching || isPending,
    primaryMethod,
    otherMethods,
    commitments,
  };
};

/**
 * Find matching payment methods
 */
const findMatchingPaymentMethods = (
  methodToMatch: GraphQLSupporterPaymentMethod,
  otherMethods: GraphQLSupporterPaymentMethod[],
) => {
  return otherMethods.filter((otherMethod) => {
    if (methodToMatch.accountNickname) {
      return false;
    }
    if (methodToMatch.creditCard) {
      return (
        otherMethod.creditCard?.cardType === methodToMatch.creditCard!.cardType
      );
    }
    return !!methodToMatch.ach && !!otherMethod.ach;
  });
};

export const PaymentSettings = () => {
  const { t } = useTranslation('my');
  const { isShowingSkeletons, primaryMethod, otherMethods, commitments } =
    useComponentData();

  return (
    <>
      <div className="pb-comfy-xl">
        {/* Leaving these as examples Positive alert */}
        {/* <Alert
          closable
          message={
            <IntlBlock
              i18nKey="my:payments.removeMethodSuccess"
              values={{
                // TODO: Remove this when we have real data available.
                cardType: 'Visa',
                lastFour: '1234',
              }}
            />
          }
          variant="positive"
        /> */}
        {/* Negative alert */}
        {/* <Alert
          action={
            <a
              href={t('payments.removeMethodError.actionUrl')}
              rel="noreferrer"
              target="_blank"
            >
              {t('payments.removeMethodError.actionLabel')}
            </a>
          }
          dismissible
          message={t('payments.removeMethodError.message')}
          title={t('payments.removeMethodError.title')}
          variant="negative"
        /> */}
      </div>

      <div className="PaymentSettings">
        {/* Primary Method */}
        {!isShowingSkeletons &&
          !primaryMethod /* && !otherMethods.length */ && (
            <div className="SectionCardList pb-comfy-xl">
              <Alert
                variant="negative"
                message={t('payments.primaryMethodMissing')}
              />
            </div>
          )}

        <div className="pb-comfy-xl">
          <SectionCardList
            heading={t('payments.primaryMethod.heading')}
            tooltipContent={t('payments.primaryMethod.tooltip')}
          >
            {!isShowingSkeletons && !!primaryMethod && (
              <PaymentMethodCard
                paymentMethod={primaryMethod}
                commitments={commitments.filter(
                  ({ paymentMethodId }) => paymentMethodId === primaryMethod.id,
                )}
              />
            )}
            {!isShowingSkeletons && !primaryMethod && (
              <AddMethodSheet
                commitments={commitments.filter(
                  ({ paymentMethodId }) =>
                    paymentMethodId === null ||
                    !otherMethods.length ||
                    /**
                     * NOTE: this loop may be unnecessary but will make sure only commitments
                     * that should be tied to this payment method will display
                     */
                    !otherMethods.some(({ id }) => id === paymentMethodId),
                )}
                openTrigger={
                  <AddPaymentMethodButton purpose="negative">
                    <AddPaymentMethodButtonLabel>
                      {t('payments.addPrimary')}
                    </AddPaymentMethodButtonLabel>
                    <AddPaymentMethodButtonDescription>
                      {t('payments.addPrimaryMessage')}
                    </AddPaymentMethodButtonDescription>
                  </AddPaymentMethodButton>
                }
              />
            )}
            {isShowingSkeletons && <Skeleton height={180} />}
          </SectionCardList>
        </div>

        {/* Other Methods */}
        {!isShowingSkeletons && (primaryMethod || !!otherMethods.length) && (
          <div
            className="pt-comfy-xl"
            // TODO: Need border utils.
            style={{
              borderTop: '1px solid var(--color-container-border-neutral-mild)',
            }}
          >
            <SectionCardList
              heading={t('payments.otherMethods.heading')}
              tooltipContent={t('payments.otherMethods.tooltip')}
            >
              {!!otherMethods.length &&
                otherMethods.map((method) => (
                  <PaymentMethodCard
                    paymentMethod={method}
                    matchingMethods={findMatchingPaymentMethods(method, [
                      primaryMethod || ([] as GraphQLSupporterPaymentMethod),
                      ...otherMethods,
                    ])}
                    commitments={commitments.filter(
                      ({ paymentMethodId }) => paymentMethodId === method.id,
                    )}
                  />
                ))}

              {primaryMethod && (
                <AddMethodSheet
                  openTrigger={
                    <AddPaymentMethodButton>
                      <AddPaymentMethodButtonLabel>
                        {t('payments.addPaymentMethod')}
                      </AddPaymentMethodButtonLabel>
                    </AddPaymentMethodButton>
                  }
                />
              )}
            </SectionCardList>
          </div>
        )}
      </div>
    </>
  );
};
